/**
 * Getting Mapbox GL into the page, once.
 *
 * Lifted out of CrmCallMap when a second screen needed a map. Two
 * copies of this would be two copies of the retry handling below, and
 * the one that did not get the fix is the one that leaves somebody
 * looking at a silent grey box.
 *
 * Served from /vendor rather than Mapbox's CDN: the app is installable
 * and the map has to come up on a connection that may only reach us.
 */
export const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN
  || import.meta.env.VITE_MAPBOX_ACCESS_TOKEN
  || '';

// ─────────────────────────────────────────────────────────────────────────────
// The base layer
// ─────────────────────────────────────────────────────────────────────────────
// Mays: "i think i want to change the base layer of the map to give it
// more pop and visually appealing." Every map in the app drew Mapbox's
// pale Light style. Satellite with streets is the default now — the
// land itself, with roads and names over it — and a control on each
// map offers the others, remembered per browser.
//
// A style change is a page reload, not a live swap. Each map adds its
// own sources and layers once, on load; Mapbox's setStyle throws them
// all away, and four maps re-adding their layers on every style load
// is four more ways to end up looking at a silent base map. The reload
// is a second, and it is correct on every page without touching any of
// them.
//
// VITE_MAPBOX_STYLE, when it is set, is offered as one more choice
// ("Custom") rather than silently winning: the point of the ask was to
// get off the style the environment had pinned.
// ─────────────────────────────────────────────────────────────────────────────
export const MAP_BASE_STYLE_STORAGE_KEY = 'map.baseStyle';
export const DEFAULT_MAP_BASE_STYLE_ID = 'satellite';
export const MAP_BASE_STYLES = Object.freeze([
  { id: 'satellite', label: 'Satellite', title: 'Satellite imagery with roads and names', url: 'mapbox://styles/mapbox/satellite-streets-v12' },
  { id: 'outdoors', label: 'Terrain', title: 'Terrain, contours and land cover', url: 'mapbox://styles/mapbox/outdoors-v12' },
  { id: 'streets', label: 'Streets', title: 'Roads and places, in colour', url: 'mapbox://styles/mapbox/streets-v12' },
  { id: 'light', label: 'Light', title: 'The pale base the app used to draw', url: 'mapbox://styles/mapbox/light-v11' },
  { id: 'dark', label: 'Dark', title: 'A dark base for the overlays to glow on', url: 'mapbox://styles/mapbox/dark-v11' },
]);
const CUSTOM_STYLE_URL = String(import.meta.env.VITE_MAPBOX_STYLE || '').trim();

/** The choices on offer, with the environment's own style last when it has one. */
export function mapBaseStyleOptions(customUrl = CUSTOM_STYLE_URL) {
  const custom = String(customUrl || '').trim();
  if (!custom || MAP_BASE_STYLES.some((s) => s.url === custom)) return [...MAP_BASE_STYLES];
  return [...MAP_BASE_STYLES, { id: 'custom', label: 'Custom', title: custom, url: custom }];
}

function styleStorage(storage) {
  if (storage) return storage;
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
}

export function readMapBaseStyleId(storage, { customUrl = CUSTOM_STYLE_URL, fallback = DEFAULT_MAP_BASE_STYLE_ID } = {}) {
  const store = styleStorage(storage);
  const options = mapBaseStyleOptions(customUrl);
  let raw = null;
  try { raw = store ? store.getItem(MAP_BASE_STYLE_STORAGE_KEY) : null; } catch { raw = null; }
  const id = typeof raw === 'string' ? raw.trim() : '';
  return options.some((s) => s.id === id) ? id : fallback;
}

export function writeMapBaseStyleId(storage, id) {
  const store = styleStorage(storage);
  if (!store) return;
  try {
    if (!id || id === DEFAULT_MAP_BASE_STYLE_ID) store.removeItem(MAP_BASE_STYLE_STORAGE_KEY);
    else store.setItem(MAP_BASE_STYLE_STORAGE_KEY, String(id));
  } catch { /* quota — a remembered base layer is not worth failing a render over */ }
}

export function mapBaseStyleUrl(id, customUrl = CUSTOM_STYLE_URL) {
  const options = mapBaseStyleOptions(customUrl);
  return (options.find((s) => s.id === id) || options.find((s) => s.id === DEFAULT_MAP_BASE_STYLE_ID)).url;
}

/** The style every map constructs with. Read once, when the page loads. */
export const MAPBOX_STYLE = mapBaseStyleUrl(readMapBaseStyleId());

/**
 * A Mapbox control: one button per base layer, the current one lit.
 * Picking one is remembered and the page reloads into it. `reload` is
 * injectable so the control can be exercised without a browser.
 */
export function createBaseStyleControl({ storage = null, reload = () => window.location.reload(), customUrl = CUSTOM_STYLE_URL } = {}) {
  let container = null;
  return {
    onAdd() {
      const current = readMapBaseStyleId(storage, { customUrl });
      container = document.createElement('div');
      container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
      container.setAttribute('data-map-base-style', current);
      for (const option of mapBaseStyleOptions(customUrl)) {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('data-map-base-style-option', option.id);
        button.title = option.title;
        button.setAttribute('aria-label', `Base layer: ${option.label}`);
        button.setAttribute('aria-pressed', option.id === current ? 'true' : 'false');
        button.textContent = option.label;
        button.style.cssText = `width:auto;min-width:29px;height:29px;padding:0 8px;font-size:11px;font-weight:${option.id === current ? 700 : 500};`
          + `color:${option.id === current ? '#0f172a' : '#475569'};background:${option.id === current ? '#e2e8f0' : '#fff'};`;
        button.addEventListener('click', () => {
          if (option.id === current) return;
          writeMapBaseStyleId(storage, option.id);
          reload();
        });
        container.appendChild(button);
      }
      return container;
    },
    onRemove() {
      container?.parentNode?.removeChild(container);
      container = null;
    },
  };
}

const SCRIPT_ID = 'crm-mapbox-gl-js';
const CSS_ID = 'crm-mapbox-gl-css';
const SCRIPT_SRC = '/vendor/mapbox-gl.js';
const CSS_HREF = '/vendor/mapbox-gl.css';
const LOAD_TIMEOUT_MS = 15000;

export function loadMapboxGl() {
  if (typeof window === 'undefined') return Promise.reject(new Error('Mapbox GL requires a browser'));
  if (window.mapboxgl) return Promise.resolve(window.mapboxgl);

  if (!document.getElementById(CSS_ID)) {
    const link = document.createElement('link');
    link.id = CSS_ID;
    link.rel = 'stylesheet';
    link.href = CSS_HREF;
    document.head.appendChild(link);
  }

  return new Promise((resolve, reject) => {
    // A script tag left behind by an earlier failed or hung load never
    // fires its events again — waiting on it kept the map a silent grey
    // box forever. Start from a fresh tag every time the library isn't
    // actually present, and give up loudly instead of never.
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      if (existing.dataset.loaded === 'true' && window.mapboxgl) {
        resolve(window.mapboxgl);
        return;
      }
      existing.remove();
    }
    const script = document.createElement('script');
    const timer = setTimeout(() => {
      reject(new Error('The map engine did not load in time — check your connection and reload the page.'));
    }, LOAD_TIMEOUT_MS);
    script.addEventListener('load', () => {
      clearTimeout(timer);
      script.dataset.loaded = 'true';
      if (window.mapboxgl) resolve(window.mapboxgl);
      else reject(new Error('Mapbox GL did not initialize'));
    }, { once: true });
    script.addEventListener('error', () => {
      clearTimeout(timer);
      script.remove();
      reject(new Error('Could not load the map engine (/vendor/mapbox-gl.js). Reload the page to retry.'));
    }, { once: true });
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    document.head.appendChild(script);
  });
}
